import {
  Flame,
  Sun,
  Sparkles,
  Wind,
  Flower2,
  Leaf,
  Moon,
  Waves,
  Activity,
  Heart,
  CircleDot,
  Droplets,
  Sprout,
  ShieldCheck,
  Zap,
} from "lucide-react";

import { attachIngredientLibraries } from "./ingredientLibraries";

/** @typedef {'gold'|'purple'|'both'} SuggestedBases */

const ICON_MAP = {
  Flame,
  Sun,
  Sparkles,
  Wind,
  Flower2,
  Leaf,
  Moon,
  Waves,
  Activity,
  Heart,
  CircleDot,
  Droplets,
  Sprout,
};

/**
 * Decorative category signpost for builder / library cards (calm forest–gold tone; not the per-ingredient flavour icon).
 * @param {{ category?: string, routineTags?: string[] }} ing
 */
export function getCategorySignpostIcon(ing) {
  const cat = ing.category || "";
  const tagBlob = (ing.routineTags || []).join(" ").toLowerCase();
  if (/glow|colour|color|skin|beauty/i.test(tagBlob)) return Sparkles;
  if (cat === "Calm Rituals" || /\bcalm\b|evening|rest\b/i.test(tagBlob)) return Moon;
  if (cat === "Sea Botanicals") return Waves;
  if (cat === "Roots") return Sprout;
  if (cat === "Warming Spices") return Flame;
  if (cat === "Superfood Powders") return Zap;
  if (cat === "Seasonal Wellness") return ShieldCheck;
  if (cat === "Popular Herbs") return Leaf;
  if (cat === "Botanicals") return Flower2;
  return Leaf;
}

/** @type {string[]} */
export const INGREDIENT_CATEGORIES = [
  "Popular Herbs",
  "Roots",
  "Botanicals",
  "Warming Spices",
  "Sea Botanicals",
  "Superfood Powders",
  "Calm Rituals",
  "Seasonal Wellness",
];

/** Launch herbs customers can add to custom blends (500ml / 1L). Intentionally small for operations. */
export const BLEND_AVAILABLE_IDS = new Set([
  "ginger",
  "turmeric",
  "cinnamon",
  "elderberry",
  "hibiscus",
  "moringa",
  "bladderwrack",
  "burdock-root",
  "nettle",
  "dandelion-root",
  "matcha-green-tea",
  "chia",
  "hemp-seed",
  "milk-thistle",
  "flaxseed",
  "green-tea-extract",
  "spirulina",
  "beetroot-powder",
]);

/** @deprecated Use BLEND_AVAILABLE_IDS */
export const LAUNCH_BUILDER_IDS = BLEND_AVAILABLE_IDS;

const LAUNCH_TAB_DAILY = new Set(["ginger", "turmeric", "cinnamon", "moringa", "chia", "flaxseed", "hemp-seed", "dandelion-root"]);
const LAUNCH_TAB_SKIN = new Set(["hibiscus", "elderberry", "nettle", "turmeric", "beetroot-powder"]);
const LAUNCH_TAB_ENERGY = new Set(["matcha-green-tea", "green-tea-extract", "beetroot-powder", "moringa", "spirulina"]);
const LAUNCH_TAB_GUT = new Set(["burdock-root", "ginger", "cinnamon", "flaxseed", "chia", "dandelion-root"]);
const LAUNCH_TAB_CALM = new Set(["milk-thistle", "nettle", "hibiscus"]);
const LAUNCH_TAB_MINERAL = new Set(["bladderwrack", "spirulina", "moringa", "nettle"]);
const LAUNCH_TAB_EARTHY = new Set(["turmeric", "burdock-root", "cinnamon", "ginger", "nettle"]);

/** Homepage builder featured strip (subset of blend-available launch herbs). */
export const LAUNCH_FAVOURITE_IDS = [
  "ginger",
  "turmeric",
  "cinnamon",
  "elderberry",
  "hibiscus",
  "moringa",
  "bladderwrack",
  "burdock-root",
];

/** Builder explorer tabs (blend-available launch herbs only). */
export const BUILDER_EXPLORE_FILTERS = [
  { id: "All", label: "All launch herbs", match: (r) => !!r.blendAvailable },
  { id: "Daily", label: "Everyday picks", match: (r) => LAUNCH_TAB_DAILY.has(r.id) },
  { id: "Skin", label: "Colour & glass routines", match: (r) => LAUNCH_TAB_SKIN.has(r.id) },
  { id: "Energy", label: "Morning refresh", match: (r) => LAUNCH_TAB_ENERGY.has(r.id) },
  { id: "Gut", label: "Roots & kitchen comfort", match: (r) => LAUNCH_TAB_GUT.has(r.id) },
  { id: "Calm", label: "Calm routine", match: (r) => LAUNCH_TAB_CALM.has(r.id) },
  { id: "Mineral", label: "Mineral-rich", match: (r) => LAUNCH_TAB_MINERAL.has(r.id) },
  { id: "Earthy", label: "Earthy / strong flavour", match: (r) => LAUNCH_TAB_EARTHY.has(r.id) },
];

/** @deprecated Use BUILDER_EXPLORE_FILTERS */
export const BUILDER_ROUTINE_FILTERS = BUILDER_EXPLORE_FILTERS;

/** @deprecated Use LAUNCH_FAVOURITE_IDS */
export const BUILDER_FEATURED_IDS = LAUNCH_FAVOURITE_IDS;

/** Ingredient library page filters (SEO library includes learn-only entries). */
export const INGREDIENT_LIBRARY_FILTERS = [
  { id: "All", label: "All ingredients" },
  { id: "blend", label: "Available for custom blends", match: (i) => i.blendAvailable },
  { id: "learn", label: "Learn only", match: (i) => i.libraryOnly },
  { id: "roots", label: "Roots", match: (i) => i.category === "Roots" },
  { id: "sea", label: "Sea botanicals", match: (i) => i.category === "Sea Botanicals" },
  {
    id: "seeds",
    label: "Seeds & greens",
    match: (i) =>
      i.category === "Superfood Powders" ||
      ["chia", "hemp-seed", "flaxseed", "spirulina", "moringa", "nettle"].includes(i.id),
  },
  { id: "spices", label: "Spices", match: (i) => i.category === "Warming Spices" },
];

/** @param {string} file filename under public/images (slug style, no spaces). */
const pubIngredientImage = (file) => `${process.env.PUBLIC_URL || ""}/images/${file}`;

/** Remote fallbacks only when no matching Moss Matrix render exists in public/images. */
const IMG = {
  herb: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=400&q=80",
  spice: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=400&q=80",
  powder: "https://images.unsplash.com/photo-1615485500834-bc10199bc727?auto=format&fit=crop&w=400&q=80",
};

/** Use in UI when `ing.image` is missing so img src is never empty. */
export const INGREDIENT_IMAGE_FALLBACK = IMG.herb;

/**
 * Rich educational blocks (library v2). See `ingredientLibraries.js` for shape and defaults.
 * @typedef {Object} IngredientLibraryRecord
 * @property {{ title?: string, bullets: string[] }} glance
 * @property {string[]} traditionalParagraphs
 * @property {{ routine: string, taste: string, pairing: string }} whyPeopleChooseIt
 * @property {string[]} nutrientsOrCompounds
 * @property {{ show: boolean, title?: string, paragraphs: string[] }} [detoxFraming]
 * @property {string[]} tasteInGel
 * @property {{ label: string, reason: string, caution?: string }[]} pairings
 * @property {{ intro: string, placeholders: string[] }} communityNotes
 * @property {string[]} cautionBullets
 * @property {{ q: string, a: string }[]} faqs
 * @property {string} [seoSummary]
 * @property {{ label: string, href: string }[]} [ctaLinks]
 */

/**
 * @typedef {Object} IngredientRecord
 * @property {string} id
 * @property {string} slug
 * @property {string} name
 * @property {string} category
 * @property {'herb'|'root'|'spice'|'botanical'|'powder'|'sea'} type
 * @property {boolean} blendAvailable — can be added to custom blends at checkout
 * @property {boolean} launchHerb — approved launch herb (same as blendAvailable for now)
 * @property {boolean} libraryOnly — educational library only, not in builder
 * @property {boolean} builderFeatured — shown in homepage launch favourites strip
 * @property {boolean} builderAllowed — @deprecated use blendAvailable
 * @property {boolean} availableInBuilder — @deprecated use blendAvailable
 * @property {string} flavour
 * @property {string} colourHint
 * @property {string} shortDescription
 * @property {string} traditionalUse
 * @property {string} whyAddToSeaMoss
 * @property {string} bestWith
 * @property {SuggestedBases} suggestedBases
 * @property {string[]} routineTags
 * @property {string} cautions
 * @property {string} image
 * @property {string} [imageAlt] — descriptive alt when image is a Moss Matrix still life
 * @property {string} seoTitle
 * @property {string} seoDescription
 * @property {string} noteLine
 * @property {string} color
 * @property {keyof typeof ICON_MAP} iconKey
 * @property {IngredientLibraryRecord} [library]
 */

/** @type {IngredientRecord[]} */
const _INGREDIENT_CATALOG_RAW = [
  {
    id: "ginger",
    slug: "ginger",
    name: "Ginger",
    category: "Popular Herbs",
    type: "herb",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Warm, zesty, slightly sweet heat",
    colourHint: "Pale gold infusion",
    noteLine: "Warming · Bright",
    color: "#D89A4A",
    iconKey: "Flame",
    image: pubIngredientImage("ginger.png"),
    imageAlt: "Fresh ginger root and ginger powder styled for Moss Matrix",
    shortDescription:
      "Ginger brings warmth, spice, and brightness. Often chosen for simple everyday habits and popular stirred into teas, juices, coconut water, or morning oats.",
    traditionalUse: "Traditionally used in kitchens across Asia and beyond as a rhizome for teas, broths, and preserves.",
    whyAddToSeaMoss:
      "Ginger wakes up the palate beside mineral-rich sea moss. Shipped custom gels use powdered botanicals only; add citrus, juice, or fruit at home.",
    bestWith: "Gold or purple base, citrus juices, chai-style spices, oats after cooking, coconut water.",
    suggestedBases: "both",
    routineTags: ["morning", "warming", "kitchen rituals"],
    cautions:
      "May cause stomach upset or heartburn in some people. If you take medications (especially blood thinners) or are unsure, speak with a healthcare professional.",
    seoTitle: "Ginger | MossMatrix Ingredient Library",
    seoDescription:
      "Learn how ginger fits herb-based MossMatrix sea moss gels: flavour profile, traditional use, and routine-based wording. This is not medical advice.",
  },
  {
    id: "turmeric",
    slug: "turmeric",
    name: "Turmeric",
    category: "Popular Herbs",
    type: "herb",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Earthy, warm, slightly bitter",
    colourHint: "Golden ochre",
    noteLine: "Earthy · Golden",
    color: "#C7A86D",
    iconKey: "Sun",
    image: pubIngredientImage("turmeric.png"),
    imageAlt: "Turmeric root and turmeric powder styled for Moss Matrix",
    shortDescription:
      "Turmeric has a bold golden colour and an earthy, warming flavour. Often chosen for golden-toned blends and simple kitchen rituals as part of an everyday routine.",
    traditionalUse: "Traditionally used as a colouring and aromatic rhizome in curries, tonics, and golden milks.",
    whyAddToSeaMoss:
      "Adds depth and colour on the spoon. Shipped custom gels use powdered botanicals only (no fresh fruit or vegetables in the jar); stir finished gel into oats, tea, coconut milk, or smoothies at home if you like.",
    bestWith: "Gold base, black pepper in warming trios, oats, shakes at home.",
    suggestedBases: "both",
    routineTags: ["golden hour", "kitchen colour"],
    cautions:
      "May cause digestive upset in sensitive stomachs. If you take certain medications (including blood thinners) or are unsure, speak with a GP or pharmacist.",
    seoTitle: "Turmeric | MossMatrix Ingredient Library",
    seoDescription:
      "Turmeric in MossMatrix herb-based gels: flavour, routine tags, and honest education. Food variety only, not cures or medical claims.",
  },
  {
    id: "elderberry",
    slug: "elderberry",
    name: "Elderberry",
    category: "Seasonal Wellness",
    type: "botanical",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Deep, tart, berry-like cordial notes",
    colourHint: "Ruby plum",
    noteLine: "Tart · Seasonal",
    color: "#5A2A4A",
    iconKey: "Sparkles",
    image: pubIngredientImage("elderberry.png"),
    imageAlt: "Elderberries styled for Moss Matrix ingredient library",
    shortDescription:
      "Elderberry is often chosen in cooler months for its bold, tart, cordial-like depth. We use properly prepared botanical powder only, never raw or unripe fruit.",
    traditionalUse: "Traditionally used in syrups and cordials in European pantry culture.",
    whyAddToSeaMoss:
      "Pairs well with purple sea moss gel for a botanical berry-style depth. Custom shipped gels stay powder-based for stability; add fresh fruit at home in smoothies or shakes if you wish.",
    bestWith: "Purple base, hibiscus, rosehip, ginger.",
    suggestedBases: "purple",
    routineTags: ["seasonal", "evening cordial"],
    cautions:
      "Only properly prepared elderberry belongs in food products, never raw or unripe elderberry. If pregnant, breastfeeding, on medication, or unsure, seek professional advice.",
    seoTitle: "Elderberry | MossMatrix Ingredient Library",
    seoDescription:
      "Elderberry education for MossMatrix customers: tart profile, seasonal routines, and safety notes for herb-based blends only.",
  },
  {
    id: "hibiscus",
    slug: "hibiscus",
    name: "Hibiscus",
    category: "Botanicals",
    type: "botanical",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Tart cranberry-like florals",
    colourHint: "Crimson pink",
    noteLine: "Tart · Floral",
    color: "#A93448",
    iconKey: "Flower2",
    image: pubIngredientImage("hibiscus.png"),
    imageAlt: "Hibiscus petals and herbal textures styled for Moss Matrix",
    shortDescription:
      "Hibiscus brings a lively tartness that reads beautifully in purple-forward blends, commonly enjoyed in iced teas and ruby-toned drinks.",
    traditionalUse: "Traditionally steeped as a cooling tea in many warm-climate cultures.",
    whyAddToSeaMoss:
      "Adds a floral-tart lift and ruby colour on the spoon. Powders only in shipped gel; coconut water, juice, or iced tea are easy partners when you mix at home.",
    bestWith: "Purple base, elderberry, rosehip.",
    suggestedBases: "purple",
    routineTags: ["afternoon", "colour rituals"],
    cautions: "Very tart, start with small amounts. If you manage low blood pressure or take medications, speak with a healthcare professional.",
    seoTitle: "Hibiscus | MossMatrix Ingredient Library",
    seoDescription:
      "Hibiscus flavour profile and routine ideas with MossMatrix sea moss gel. Educational food copy only, not medical advice.",
  },
  {
    id: "moringa",
    slug: "moringa",
    name: "Moringa",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Green, grassy, slightly bitter",
    colourHint: "Leaf green",
    noteLine: "Green · Mineral feel",
    color: "#3F6B3A",
    iconKey: "Leaf",
    image: pubIngredientImage("moringa.png"),
    imageAlt: "Moringa green powder and linen still life styled for Moss Matrix",
    shortDescription:
      "Moringa is often chosen when people want a mineral-rich, green spoonful as part of a balanced lifestyle. It is popular in shakes and morning bowls at home.",
    traditionalUse: "Traditionally used as a leafy green and dried powder in many regional cuisines.",
    whyAddToSeaMoss:
      "Gives an earthy, vivid green note beside a mild gold base. Powdered botanicals only in what we ship; many customers fold a spoonful into shakes or oats at home.",
    bestWith: "Gold base, spirulina, nettle, ginger.",
    suggestedBases: "gold",
    routineTags: ["morning", "green routines"],
    cautions: "Start small if new to bitter greens. If pregnant or on medication, speak with a healthcare professional.",
    seoTitle: "Moringa | MossMatrix Ingredient Library",
    seoDescription:
      "Moringa in MossMatrix herb-based sea moss gels: green profile, routine language, and safe-use notes.",
  },
  {
    id: "cinnamon",
    slug: "cinnamon",
    name: "Cinnamon",
    category: "Warming Spices",
    type: "spice",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Sweet warmth, soft spice",
    colourHint: "Warm brown",
    noteLine: "Sweet spice",
    color: "#8B4A2B",
    iconKey: "Wind",
    image: pubIngredientImage("cinnamon.png"),
    imageAlt: "Cinnamon sticks and ground cinnamon styled for Moss Matrix",
    shortDescription:
      "Cinnamon is commonly added for cosy, sweet-spice flavour in oats, warm milk, or evening cups.",
    traditionalUse: "Traditionally used in baking, masalas, and spiced teas worldwide.",
    whyAddToSeaMoss:
      "Rounds out herbal edges and pairs well with sea moss gel in porridge-style habits at home.",
    bestWith: "Gold or purple base, ashwagandha, chamomile, ginger.",
    suggestedBases: "both",
    routineTags: ["evening", "warming"],
    cautions:
      "High intakes of cassia-type cinnamon may not suit everyone. If you have liver concerns, take medications, or are unsure, speak with a healthcare professional.",
    seoTitle: "Cinnamon | MossMatrix Ingredient Library",
    seoDescription:
      "Cinnamon flavour profile for MossMatrix sea moss gels, with warming spice notes and sensible cautions.",
  },
  {
    id: "ashwagandha",
    slug: "ashwagandha",
    name: "Ashwagandha",
    category: "Calm Rituals",
    type: "herb",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Earthy, slightly bitter, grounding",
    colourHint: "Warm tan",
    noteLine: "Evening · Earthy",
    color: "#7A6346",
    iconKey: "Moon",
    image: pubIngredientImage("ashwagandha.png"),
    imageAlt: "Ashwagandha root and powder on a textured tabletop styled for Moss Matrix",
    shortDescription:
      "Ashwagandha root powder is often chosen for quiet evenings when people want an earthy, grounding spoonful as part of a wind-down habit.",
    traditionalUse: "Traditionally used in Ayurvedic herbal traditions as a root preparation.",
    whyAddToSeaMoss:
      "Pairs well with purple or gold bases when you want a softer, grounding spoonful as part of a wind-down habit.",
    bestWith: "Purple base, chamomile, cinnamon.",
    suggestedBases: "purple",
    routineTags: ["evening", "calm rituals"],
    cautions:
      "Avoid during pregnancy and breastfeeding unless directed otherwise. Use caution with thyroid or liver concerns and with many medications. Speak with a healthcare professional if unsure.",
    seoTitle: "Ashwagandha | MossMatrix Ingredient Library",
    seoDescription:
      "Ashwagandha education for MossMatrix blends: evening routines, cautions, and herb-only gel philosophy.",
  },
  {
    id: "bladderwrack",
    slug: "bladderwrack",
    name: "Bladderwrack",
    category: "Sea Botanicals",
    type: "sea",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Briny, mineral, oceanic",
    colourHint: "Deep olive",
    noteLine: "Marine · Mineral",
    color: "#2E4A3F",
    iconKey: "Waves",
    image: pubIngredientImage("bladderwrack.png"),
    imageAlt: "Bladderwrack seaweed arrangement styled for Moss Matrix ingredient library",
    shortDescription:
      "Bladderwrack is a sea botanical often layered with sea moss for a briny, ocean-forward profile. Popular with people who already enjoy marine notes in the kitchen.",
    traditionalUse: "Traditionally used in coastal cuisines and herbal traditions as dried edible seaweed.",
    whyAddToSeaMoss:
      "Adds marine depth beside sea moss. Still a food-style blend meant to be enjoyed in small amounts as part of a balanced lifestyle.",
    bestWith: "Gold base, nettle, ginger.",
    suggestedBases: "gold",
    routineTags: ["mineral routines", "coastal"],
    cautions:
      "Contains iodine and other sea minerals. Anyone pregnant, breastfeeding, taking medication, managing thyroid issues, or unsure should speak with a healthcare professional before use.",
    seoTitle: "Bladderwrack | MossMatrix Ingredient Library",
    seoDescription:
      "Bladderwrack and sea moss: iodine caution, marine flavour notes, and MossMatrix education-only copy.",
  },
  {
    id: "maca",
    slug: "maca",
    name: "Maca",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: true,
    builderAllowed: true,
    flavour: "Malty, caramel-nutty, earthy",
    colourHint: "Butterscotch beige",
    noteLine: "Malty · Morning",
    color: "#B8956A",
    iconKey: "Activity",
    image: IMG.powder,
    shortDescription:
      "Maca root powder is commonly added to morning oats and shakes for its malty, earthy sweetness. A pantry flavour note, not a stimulant story.",
    traditionalUse: "Traditionally used in the Andes as a roasted flour and tonic-style ingredient.",
    whyAddToSeaMoss:
      "Pairs well with gold sea moss gel in shakes and oats at home when you want a rounded, nutty note.",
    bestWith: "Gold base, moringa, ginger, cacao.",
    suggestedBases: "gold",
    routineTags: ["morning"],
    cautions: "If you have hormone-sensitive conditions or take medications, speak with a healthcare professional before regular use.",
    seoTitle: "Maca | MossMatrix Ingredient Library",
    seoDescription:
      "Maca flavour profile with MossMatrix sea moss gel: morning routine language and safety notes.",
  },
  {
    id: "burdock-root",
    slug: "burdock-root",
    name: "Burdock Root",
    category: "Roots",
    type: "root",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Earthy, mildly sweet, quiet bitter root",
    colourHint: "Warm umber",
    noteLine: "Earthy · Root",
    color: "#6B5344",
    iconKey: "Sprout",
    image: pubIngredientImage("burdock-root.png"),
    imageAlt: "Burdock root and leaves still life styled for Moss Matrix ingredient library",
    shortDescription:
      "Burdock root is often chosen for warm, earthy daily routines when people want a quiet base note. Still just botanical flavour in the jar.",
    traditionalUse: "Traditionally used in teas and broths in East Asian and European herbal pantry culture.",
    whyAddToSeaMoss:
      "Adds grounding depth to custom gels alongside ginger or cinnamon, and pairs well with gold bases.",
    bestWith: "Gold base, dandelion root, ginger.",
    suggestedBases: "gold",
    routineTags: ["warming kitchen", "slow rituals"],
    cautions: "If you are dehydrated, take diuretics, manage blood sugar, or are unsure, speak with a healthcare professional.",
    seoTitle: "Burdock Root | MossMatrix Ingredient Library",
    seoDescription:
      "Burdock root in MossMatrix botanical blends: earthy profile, routine wording, and cautions.",
  },
  {
    id: "dandelion-root",
    slug: "dandelion-root",
    name: "Dandelion Root",
    category: "Roots",
    type: "root",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Roasted, tea-like bitter with mellow depth",
    colourHint: "Deep tan",
    noteLine: "Roasted · Bitter",
    color: "#8B7355",
    iconKey: "Sprout",
    image: pubIngredientImage("dandelion-root.png"),
    imageAlt: "Dandelion root and leaves styled for Moss Matrix ingredient library",
    shortDescription:
      "Dandelion root is commonly roasted for tea-like depth. In our gels it is there for flavour and aroma on the spoon, like a kitchen tea habit.",
    traditionalUse: "Traditionally enjoyed as a roasted coffee-style tea in European herbal kitchens.",
    whyAddToSeaMoss:
      "Pairs well with cinnamon and ginger for a warming spoonful in gold-base gels.",
    bestWith: "Gold base, burdock root, ginger.",
    suggestedBases: "gold",
    routineTags: ["afternoon", "warming"],
    cautions:
      "Dandelion may not suit everyone. Read the cautions below and speak with a qualified professional if you are unsure. This is not medical advice.",
    seoTitle: "Dandelion Root | Moss Matrix ingredient library",
    seoDescription:
      "Educational guide to dandelion root with Moss Matrix: roasted flavour, traditional herbal context, pairings, and safe cautions. Food and routine information only.",
  },
  {
    id: "nettle",
    slug: "nettle",
    name: "Nettle",
    category: "Popular Herbs",
    type: "herb",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Bold green, mineral-leaning, slightly vegetal",
    colourHint: "Deep leaf green",
    noteLine: "Green · Mineral",
    color: "#3D5C3A",
    iconKey: "Leaf",
    image: pubIngredientImage("nettle.png"),
    imageAlt: "Nettle leaves and herbal textures styled for Moss Matrix ingredient library",
    shortDescription:
      "Nettle leaf has a vivid green profile and a bold, mineral-leaning taste. Popular stirred into plant milks, shakes, or oats at home for a green-kitchen feel.",
    traditionalUse: "Traditionally cooked as a pot herb and steeped as tea in many regions.",
    whyAddToSeaMoss:
      "Adds a deep green note alongside moringa or spirulina. Shipped gels are herb powders only; blend into your own smoothie or coconut water at home.",
    bestWith: "Gold base, moringa, spirulina.",
    suggestedBases: "gold",
    routineTags: ["green routines"],
    cautions: "If you take diuretics, blood pressure medicines, blood thinners, or are unsure, speak with a healthcare professional.",
    seoTitle: "Nettle | MossMatrix Ingredient Library",
    seoDescription:
      "Nettle leaf in MossMatrix sea moss gels: green flavour profile, routine tags, and cautions.",
  },
  {
    id: "chamomile",
    slug: "chamomile",
    name: "Chamomile",
    category: "Calm Rituals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Apple-hay, soft floral",
    colourHint: "Pale straw",
    noteLine: "Soft floral",
    color: "#C4B87A",
    iconKey: "Flower2",
    image: IMG.herb,
    shortDescription:
      "Chamomile is commonly chosen for quiet evening rituals. Gentle floral flavour that plays well with cinnamon.",
    traditionalUse: "Traditionally steeped as a bedtime-style tea across Europe and beyond.",
    whyAddToSeaMoss:
      "Softens the spoonful when paired with purple or gold sea moss gel for cosy evening habits.",
    bestWith: "Purple base, ashwagandha, cinnamon.",
    suggestedBases: "both",
    routineTags: ["evening"],
    cautions: "Rarely, aster-family allergies apply. If you take sedating medications or are unsure, speak with a healthcare professional.",
    seoTitle: "Chamomile | MossMatrix Ingredient Library",
    seoDescription:
      "Chamomile botanical notes for MossMatrix herb-based sea moss gels: calm routine language only.",
  },
  {
    id: "lavender",
    slug: "lavender",
    name: "Lavender",
    category: "Botanicals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Floral, aromatic, slightly resinous",
    colourHint: "Soft purple-grey",
    noteLine: "Floral · Aromatic",
    color: "#9B8FB8",
    iconKey: "Flower2",
    image: IMG.herb,
    shortDescription:
      "Lavender is popular in calm evening rituals for its perfumed floral note. A little goes a long way in gel blends.",
    traditionalUse: "Traditionally used in small amounts in baking and herbal syrups.",
    whyAddToSeaMoss:
      "Pairs sparingly with purple bases for a spa-like aroma in the spoon. Taste-first, always light-handed.",
    bestWith: "Purple base, rosehip, hibiscus.",
    suggestedBases: "purple",
    routineTags: ["evening", "aroma rituals"],
    cautions: "Very aromatic, use modestly. If pregnant, breastfeeding, or sensitive to strong botanicals, seek professional advice.",
    seoTitle: "Lavender | MossMatrix Ingredient Library",
    seoDescription:
      "Lavender education for MossMatrix blends: floral profile and light-touch use in sea moss gel.",
  },
  {
    id: "peppermint",
    slug: "peppermint",
    name: "Peppermint",
    category: "Popular Herbs",
    type: "herb",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Cool, bright menthol",
    colourHint: "Cool green",
    noteLine: "Cool · Fresh",
    color: "#5A8F6F",
    iconKey: "Leaf",
    image: IMG.herb,
    shortDescription:
      "Peppermint leaf adds a clean, cooling finish. Lovely in iced drinks, coconut water, or a simple afternoon cup at home.",
    traditionalUse: "Traditionally steeped as a refreshing tea in many cultures.",
    whyAddToSeaMoss:
      "Adds a bright top note to sea moss gel when you want freshness without fruit in the jar.",
    bestWith: "Gold base, spearmint, cacao.",
    suggestedBases: "gold",
    routineTags: ["afternoon", "refresh"],
    cautions: "May aggravate reflux in some people. If you take medications or have GERD, speak with a healthcare professional.",
    seoTitle: "Peppermint | MossMatrix Ingredient Library",
    seoDescription:
      "Peppermint in MossMatrix sea moss gels: cooling flavour profile and routine ideas.",
  },
  {
    id: "spearmint",
    slug: "spearmint",
    name: "Spearmint",
    category: "Popular Herbs",
    type: "herb",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Sweet mint, softer than peppermint",
    colourHint: "Soft green",
    noteLine: "Sweet mint",
    color: "#6FA985",
    iconKey: "Leaf",
    image: IMG.herb,
    shortDescription:
      "Spearmint offers a sweeter mint lift and is popular stirred into cool drinks, iced tea, or coconut water at home.",
    traditionalUse: "Traditionally used in teas and Middle Eastern beverages.",
    whyAddToSeaMoss:
      "Pairs well with gold gel for a gentle mint finish. Still herb-based for safer UK delivery.",
    bestWith: "Gold base, peppermint, lemon-balm.",
    suggestedBases: "gold",
    routineTags: ["afternoon"],
    cautions: "If you have reflux or take medications, speak with a healthcare professional if unsure.",
    seoTitle: "Spearmint | MossMatrix Ingredient Library",
    seoDescription:
      "Spearmint flavour notes with MossMatrix sea moss gel: herb-only blends.",
  },
  {
    id: "rosehip",
    slug: "rosehip",
    name: "Rosehip",
    category: "Botanicals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Tart berry-hip, light citrus",
    colourHint: "Rose copper",
    noteLine: "Tart · Floral",
    color: "#B85C5C",
    iconKey: "Heart",
    image: IMG.herb,
    shortDescription:
      "Rosehip is popular for tart, fruity-floral colour on the spoon. Shipped blends use dried botanical powders only, not fresh fruit.",
    traditionalUse: "Traditionally used in syrups and teas for tart, cordial-style pantry appeal.",
    whyAddToSeaMoss:
      "Pairs with hibiscus and turmeric for a berry-style botanical line in purple gels.",
    bestWith: "Purple base, elderberry, hibiscus.",
    suggestedBases: "purple",
    routineTags: ["bright colour routines"],
    cautions: "Tart acids may bother sensitive teeth or stomachs. If you take iron supplements or medications, speak with a healthcare professional.",
    seoTitle: "Rosehip | MossMatrix Ingredient Library",
    seoDescription:
      "Rosehip botanical profile for MossMatrix sea moss gels: tart floral notes, no fresh fruit in blends.",
  },
  {
    id: "lemon-balm",
    slug: "lemon-balm",
    name: "Lemon Balm",
    category: "Calm Rituals",
    type: "herb",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Citrus-herb, soft lemon",
    colourHint: "Pale leaf green",
    noteLine: "Soft citrus herb",
    color: "#9DB88E",
    iconKey: "Leaf",
    image: IMG.herb,
    shortDescription:
      "Lemon balm is often chosen for quiet afternoon rituals. Gentle citrus-herb brightness without sharp acid.",
    traditionalUse: "Traditionally steeped as a soft citrus-herb tea in European kitchens.",
    whyAddToSeaMoss:
      "Brightens gold-base gels when paired with mint or chamomile for a garden-cup vibe.",
    bestWith: "Gold base, spearmint, chamomile.",
    suggestedBases: "gold",
    routineTags: ["afternoon", "calm rituals"],
    cautions: "If you take sedating medications or thyroid medication, speak with a healthcare professional.",
    seoTitle: "Lemon Balm | MossMatrix Ingredient Library",
    seoDescription:
      "Lemon balm education for MossMatrix botanical blends: soft citrus notes and cautions.",
  },
  {
    id: "clove",
    slug: "clove",
    name: "Clove",
    category: "Warming Spices",
    type: "spice",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Intense warm spice, warm sweet depth",
    colourHint: "Deep brown-red",
    noteLine: "Bold spice",
    color: "#5C3024",
    iconKey: "CircleDot",
    image: IMG.spice,
    shortDescription:
      "Clove is potent. A tiny amount adds festive warmth and suits seasonal baking-style habits in the jar.",
    traditionalUse: "Traditionally used in mulled drinks and baking spice mixes.",
    whyAddToSeaMoss:
      "Use sparingly with cinnamon for a chai-like accent in gold gels. Always taste-led.",
    bestWith: "Gold base, cinnamon, ginger.",
    suggestedBases: "gold",
    routineTags: ["seasonal"],
    cautions:
      "Very strong and easy to overdo. Clove oil is not the same as food-grade powder. If you take blood thinners or are unsure, speak with a healthcare professional.",
    seoTitle: "Clove | MossMatrix Ingredient Library",
    seoDescription:
      "Clove spice notes for MossMatrix sea moss gels: potency cautions and seasonal routines.",
  },
  {
    id: "cardamom",
    slug: "cardamom",
    name: "Cardamom",
    category: "Warming Spices",
    type: "spice",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Resinous citrus, sweet spice",
    colourHint: "Sage green husk tones",
    noteLine: "Aromatic spice",
    color: "#6B8060",
    iconKey: "Wind",
    image: IMG.spice,
    shortDescription:
      "Cardamom is commonly added for perfumed, sweet-spice aroma. Lovely with gold gels and milky drinks at home.",
    traditionalUse: "Traditionally used in masala chai and Nordic baked goods.",
    whyAddToSeaMoss:
      "Adds aromatic lift beside ginger or cinnamon for premium chai-style habits, without medical framing.",
    bestWith: "Gold base, ginger, cinnamon.",
    suggestedBases: "gold",
    routineTags: ["morning", "warming"],
    cautions: "If you have gallstones or take medications, speak with a healthcare professional if unsure.",
    seoTitle: "Cardamom | MossMatrix Ingredient Library",
    seoDescription:
      "Cardamom flavour profile with MossMatrix sea moss gel: warming spice education.",
  },
  {
    id: "nutmeg",
    slug: "nutmeg",
    name: "Nutmeg",
    category: "Warming Spices",
    type: "spice",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Warm wood, sweet musk",
    colourHint: "Warm tan",
    noteLine: "Cozy spice",
    color: "#8B7355",
    iconKey: "Moon",
    image: IMG.spice,
    shortDescription:
      "Nutmeg offers cosy, sweet-musk depth. A small pinch is plenty in gel blends.",
    traditionalUse: "Traditionally used in béchamel, bakes, and holiday spice mixes.",
    whyAddToSeaMoss:
      "Pairs with cinnamon for a porridge-style spoonful. Taste-led, always light-handed.",
    bestWith: "Gold base, cinnamon, vanilla.",
    suggestedBases: "gold",
    routineTags: ["evening"],
    cautions: "Toxic in very large amounts. Food micro-doses only. If pregnant or unsure, speak with a healthcare professional.",
    seoTitle: "Nutmeg | MossMatrix Ingredient Library",
    seoDescription:
      "Nutmeg in MossMatrix blends: cosy spice notes and a micro-dose safety reminder.",
  },
  {
    id: "vanilla",
    slug: "vanilla",
    name: "Vanilla",
    category: "Warming Spices",
    type: "spice",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Sweet balsamic, floral warmth",
    colourHint: "Cream caramel",
    noteLine: "Sweet · Soft",
    color: "#C4A57B",
    iconKey: "Wind",
    image: IMG.spice,
    shortDescription:
      "Vanilla rounds sharp edges and is popular when customers want a dessert-like spoonful without adding sugar-heavy syrups.",
    traditionalUse: "Traditionally used in custards, baking, and botanical infusions.",
    whyAddToSeaMoss:
      "Softens herbal blends and pairs with cacao or cinnamon in gold-base gels.",
    bestWith: "Gold base, cacao, nutmeg.",
    suggestedBases: "gold",
    routineTags: ["evening"],
    cautions: "If you are sensitive to alcohol-based extracts, choose powder forms suited to your needs and read labels.",
    seoTitle: "Vanilla | MossMatrix Ingredient Library",
    seoDescription:
      "Vanilla notes for MossMatrix sea moss gels: soft sweetness and routine language.",
  },
  {
    id: "cayenne",
    slug: "cayenne",
    name: "Cayenne",
    category: "Warming Spices",
    type: "spice",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Sharp capsaicin heat",
    colourHint: "Fiery red",
    noteLine: "Hot · Bold",
    color: "#C1442E",
    iconKey: "Flame",
    image: IMG.spice,
    shortDescription:
      "Cayenne brings serious heat for customers who already love chilli-forward habits in tiny amounts.",
    traditionalUse: "Traditionally used in kitchens globally for heat and colour.",
    whyAddToSeaMoss:
      "Micro-doses can lift gold gels beside turmeric. Always start smaller than you think.",
    bestWith: "Gold base, turmeric, black-pepper.",
    suggestedBases: "gold",
    routineTags: ["warming"],
    cautions:
      "Can irritate sensitive stomachs. Avoid touching eyes. If you take stomach medications or have GI conditions, speak with a healthcare professional.",
    seoTitle: "Cayenne | MossMatrix Ingredient Library",
    seoDescription:
      "Cayenne heat notes for MossMatrix sea moss gels: strong cautions and micro-dose habits.",
  },
  {
    id: "black-pepper",
    slug: "black-pepper",
    name: "Black Pepper",
    category: "Warming Spices",
    type: "spice",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Piney heat, citrus lift",
    colourHint: "Charcoal spice",
    noteLine: "Classic kitchen heat",
    color: "#3A3A3A",
    iconKey: "CircleDot",
    image: IMG.spice,
    shortDescription:
      "Black pepper is a familiar kitchen spice and is often paired with turmeric in traditional food combinations.",
    traditionalUse: "Traditionally used at the table and in masalas worldwide.",
    whyAddToSeaMoss:
      "Adds gentle heat and aroma beside turmeric and ginger. Flavour synergy language only.",
    bestWith: "Gold base, turmeric, ginger.",
    suggestedBases: "gold",
    routineTags: ["warming"],
    cautions: "Can irritate sensitive stomachs. If you take medications or have GI issues, speak with a healthcare professional.",
    seoTitle: "Black Pepper | MossMatrix Ingredient Library",
    seoDescription:
      "Black pepper in MossMatrix herb gels: warming kitchen notes and cautions.",
  },
  {
    id: "black-seed",
    slug: "black-seed",
    name: "Black Seed (Nigella)",
    category: "Popular Herbs",
    type: "herb",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Onion-pepper, slightly bitter",
    colourHint: "Ink black speckle",
    noteLine: "Savory spice seed",
    color: "#1E1E24",
    iconKey: "Sparkles",
    image: IMG.spice,
    shortDescription:
      "Black seed (nigella) is popular in Middle Eastern and South Asian pantry routines: savoury-aromatic, not sweet.",
    traditionalUse: "Traditionally sprinkled on breads and stirred into honey in folk kitchens.",
    whyAddToSeaMoss:
      "Adds speckled drama and savoury depth. Best for customers who already enjoy nigella in food.",
    bestWith: "Gold base, turmeric, honey at home (stirred in by you).",
    suggestedBases: "gold",
    routineTags: ["savoury rituals"],
    cautions: "If pregnant, breastfeeding, taking medications, or managing surgery, speak with a healthcare professional.",
    seoTitle: "Black Seed | MossMatrix Ingredient Library",
    seoDescription:
      "Black seed (nigella) education for MossMatrix sea moss gels: savoury profile and cautions.",
  },
  {
    id: "rosemary",
    slug: "rosemary",
    name: "Rosemary",
    category: "Botanicals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Pine-resin, savoury herb",
    colourHint: "Deep green",
    noteLine: "Savoury pine",
    color: "#4A5D3F",
    iconKey: "Leaf",
    image: IMG.herb,
    shortDescription:
      "Rosemary is bold. It is popular in savoury kitchen experiments when used very lightly.",
    traditionalUse: "Traditionally used in Mediterranean roasting and herbal teas.",
    whyAddToSeaMoss:
      "For adventurous palates only. A whisper pairs with black pepper or thyme; not a beginner herb in gel.",
    bestWith: "Gold base, thyme, black-pepper.",
    suggestedBases: "gold",
    routineTags: ["savoury rituals"],
    cautions: "Concentrated rosemary can be strong. If pregnant, have epilepsy, or take medications, speak with a healthcare professional.",
    seoTitle: "Rosemary | MossMatrix Ingredient Library",
    seoDescription:
      "Rosemary savoury notes for MossMatrix sea moss gels: strong flavour and cautions.",
  },
  {
    id: "thyme",
    slug: "thyme",
    name: "Thyme",
    category: "Botanicals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Earthy mint, subtle lemon",
    colourHint: "Grey-green",
    noteLine: "Earthy herb",
    color: "#6B7565",
    iconKey: "Leaf",
    image: IMG.herb,
    shortDescription:
      "Thyme offers a quiet earthy note, sometimes chosen beside rosemary for savoury kitchen blends.",
    traditionalUse: "Traditionally used in stocks, roasts, and herbal syrups.",
    whyAddToSeaMoss:
      "Use sparingly with gold gel, savoury profile for customers who want herb-forward spoons.",
    bestWith: "Gold base, rosemary, black-pepper.",
    suggestedBases: "gold",
    routineTags: ["savoury rituals"],
    cautions:
      "Food amounts only. If you are pregnant, take concentrated herbal products elsewhere, or are unsure, speak with a healthcare professional.",
    seoTitle: "Thyme | MossMatrix Ingredient Library",
    seoDescription:
      "Thyme in MossMatrix herb-based sea moss gels: earthy profile and light use.",
  },
  {
    id: "spirulina",
    slug: "spirulina",
    name: "Spirulina",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Deep marine green, slight iodine tang",
    colourHint: "Electric teal",
    noteLine: "Bold green",
    color: "#1F6F5B",
    iconKey: "Waves",
    image: IMG.powder,
    shortDescription:
      "Spirulina is popular when people want a vivid green spoonful in shakes or coconut water at home. A little powder goes a long way on colour.",
    traditionalUse: "Traditionally consumed as a dried microalgae in many modern kitchens.",
    whyAddToSeaMoss:
      "Builds a bold green line with moringa or nettle. Custom shipped gels stay powder-only; you control fruit and veg in your own glass.",
    bestWith: "Gold base, moringa, nettle.",
    suggestedBases: "gold",
    routineTags: ["green routines"],
    cautions:
      "May contain naturally occurring compounds not suited to everyone. If pregnant, autoimmune-prone, or on medication, speak with a healthcare professional.",
    seoTitle: "Spirulina | MossMatrix Ingredient Library",
    seoDescription:
      "Spirulina in MossMatrix sea moss gels: green profile, cautions, routine language.",
  },
  {
    id: "chlorella",
    slug: "chlorella",
    name: "Chlorella",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Deep green, slightly grassy",
    colourHint: "Forest green",
    noteLine: "Green microalgae",
    color: "#2A4D3A",
    iconKey: "Droplets",
    image: IMG.powder,
    shortDescription:
      "Chlorella is another green powder customers sometimes rotate with spirulina for earthy spoonful habits.",
    traditionalUse: "Traditionally used as a dried greens powder in smoothies and kitchen blends.",
    whyAddToSeaMoss:
      "Pairs with spirulina for a bold green duo. Start small for taste and colour.",
    bestWith: "Gold base, spirulina, moringa.",
    suggestedBases: "gold",
    routineTags: ["green routines"],
    cautions: "If you take blood thinners, are pregnant, or have shellfish/mold sensitivities, speak with a healthcare professional.",
    seoTitle: "Chlorella | MossMatrix Ingredient Library",
    seoDescription:
      "Chlorella education for MossMatrix sea moss gels: green powders and cautions.",
  },
  {
    id: "matcha-green-tea",
    slug: "matcha-green-tea",
    name: "Matcha / Green Tea",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Umami grass, slight bitterness",
    colourHint: "Jade green",
    noteLine: "Tea umami",
    color: "#4A6B4A",
    iconKey: "Leaf",
    image: IMG.powder,
    shortDescription:
      "Matcha and green tea powders bring gentle caffeine and umami. Often chosen for morning brew-and-go habits alongside sea moss.",
    traditionalUse: "Traditionally whisked in East Asian tea ceremony and modern lattes.",
    whyAddToSeaMoss:
      "Pairs with gold gel for a latte-style spoonful when customers want tea notes without brewing first.",
    bestWith: "Gold base, vanilla, maca.",
    suggestedBases: "gold",
    routineTags: ["morning"],
    cautions:
      "Contains caffeine. Sensitive people should time use thoughtfully. If pregnant or on stimulant-sensitive medications, speak with a healthcare professional.",
    seoTitle: "Matcha & Green Tea | MossMatrix Ingredient Library",
    seoDescription:
      "Matcha and green tea powders with MossMatrix sea moss gel: caffeine note and routine ideas.",
  },
  {
    id: "cacao",
    slug: "cacao",
    name: "Cacao",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Bitter chocolate, roasted depth",
    colourHint: "Rich brown",
    noteLine: "Chocolate depth",
    color: "#4A3228",
    iconKey: "Heart",
    image: IMG.powder,
    shortDescription:
      "Cacao is popular in evening treat rituals for bitter chocolate depth without being dessert syrup.",
    traditionalUse: "Traditionally used in Mesoamerican drinks and in modern cacao drinks (food context).",
    whyAddToSeaMoss:
      "Pairs with cinnamon and vanilla for a cosy gold-base spoonful. Still stirred into milk at home if you like.",
    bestWith: "Gold base, cinnamon, vanilla.",
    suggestedBases: "gold",
    routineTags: ["evening"],
    cautions: "Contains trace caffeine/theobromine. If migraine-prone, reflux-prone, or on MAOIs, speak with a healthcare professional.",
    seoTitle: "Cacao | MossMatrix Ingredient Library",
    seoDescription:
      "Cacao powder in MossMatrix sea moss gels: bitter chocolate notes and cautions.",
  },
  {
    id: "beetroot-powder",
    slug: "beetroot-powder",
    name: "Beetroot Powder",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Earthy beet sweetness",
    colourHint: "Magenta",
    noteLine: "Earthy sweet",
    color: "#8B2942",
    iconKey: "Heart",
    image: IMG.powder,
    shortDescription:
      "Beetroot powder paints gels magenta and is popular when customers want colour-forward spoons without fruit purée.",
    traditionalUse: "Traditionally used as a natural colour in foods and ferments.",
    whyAddToSeaMoss:
      "Pairs with purple bases for dramatic colour and an earthy-sweet profile.",
    bestWith: "Purple base, hibiscus, rosehip.",
    suggestedBases: "purple",
    routineTags: ["colour rituals"],
    cautions:
      "Rich in oxalates and natural nitrates. Not suitable for everyone. If you manage kidney stones, blood pressure, or take nitrate medications, speak with a healthcare professional.",
    seoTitle: "Beetroot Powder | MossMatrix Ingredient Library",
    seoDescription:
      "Beetroot powder colour rituals with MossMatrix sea moss gel: cautions for nitrates and oxalates.",
  },
  {
    id: "baobab",
    slug: "baobab",
    name: "Baobab",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Tart citrus, sherbet-like",
    colourHint: "Pale ivory powder",
    noteLine: "Tart citrus",
    color: "#D4C4A8",
    iconKey: "Sun",
    image: IMG.powder,
    shortDescription:
      "Baobab fruit powder is tangy and is popular in morning shake routines at home when people want citrus zip without juice in the jar.",
    traditionalUse: "Traditionally used in African beverages and dry-mix drinks.",
    whyAddToSeaMoss:
      "Brightens gold or purple gels beside berry-style botanicals. Still dried powder, not fresh fruit.",
    bestWith: "Purple base, rosehip, hibiscus.",
    suggestedBases: "both",
    routineTags: ["afternoon"],
    cautions: "High fibre and may affect digestion or medication timing. Speak with a healthcare professional if unsure.",
    seoTitle: "Baobab | MossMatrix Ingredient Library",
    seoDescription:
      "Baobab fruit powder with MossMatrix sea moss gels: tart profile, dried powder only.",
  },
  {
    id: "acerola-cherry",
    slug: "acerola-cherry",
    name: "Acerola Cherry",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Tart-sweet, fruity powder note",
    colourHint: "Soft coral",
    noteLine: "Tart fruit powder",
    color: "#E07A5F",
    iconKey: "Sparkles",
    image: IMG.powder,
    shortDescription:
      "Acerola cherry is a tart fruit powder, not fresh fruit in the jar. Many customers like that pantry-style tang with berry botanicals.",
    traditionalUse: "Traditionally used in modern drink mixes for tart flavour.",
    whyAddToSeaMoss:
      "Pairs with hibiscus and rosehip for tart lift. Still shelf-stable powder, not fresh produce.",
    bestWith: "Purple base, hibiscus, rosehip.",
    suggestedBases: "purple",
    routineTags: ["tangy routines"],
    cautions: "If you manage blood sugar, take medications, or have oxalate concerns, speak with a healthcare professional.",
    seoTitle: "Acerola Cherry Powder | MossMatrix Ingredient Library",
    seoDescription:
      "Acerola cherry powder in MossMatrix herb-based gels: tart pantry note, herb-based gel only (no fresh produce in the jar).",
  },
  {
    id: "camu-camu",
    slug: "camu-camu",
    name: "Camu Camu",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Sharp sour berry",
    colourHint: "Pale pink",
    noteLine: "Super-sour berry",
    color: "#D88FA8",
    iconKey: "Sparkles",
    image: IMG.powder,
    shortDescription:
      "Camu camu powder is intensely sour. Micro amounts suit customers who love sharp berry tang.",
    traditionalUse: "Traditionally used in Amazonian regions as a tart fruit preparation; here as dried powder only.",
    whyAddToSeaMoss:
      "Tiny pinches with purple gels and hibiscus. Still not fresh fruit, better for shipping stability.",
    bestWith: "Purple base, acerola-cherry, hibiscus.",
    suggestedBases: "purple",
    routineTags: ["tangy routines"],
    cautions: "Very acidic and may affect sensitive teeth or stomachs. If pregnant or on medications, speak with a healthcare professional.",
    seoTitle: "Camu Camu | MossMatrix Ingredient Library",
    seoDescription:
      "Camu camu powder education: tart berry powder for MossMatrix sea moss gels, not fresh fruit.",
  },
  {
    id: "flaxseed",
    slug: "flaxseed",
    name: "Flaxseed",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Nutty, slightly grassy seed",
    colourHint: "Warm brown speckle",
    noteLine: "Nutty seed",
    color: "#8B7355",
    iconKey: "Leaf",
    image: IMG.powder,
    shortDescription:
      "Ground flaxseed adds speckle and nutty body. Popular in oat-forward routines at home.",
    traditionalUse: "Traditionally used in baking and porridges globally.",
    whyAddToSeaMoss:
      "Pairs with cinnamon in gold gels for a porridge-memory flavour. Texture may read slightly grainy.",
    bestWith: "Gold base, cinnamon, chia.",
    suggestedBases: "gold",
    routineTags: ["morning"],
    cautions:
      "High fibre: hydrate well. May affect medication absorption timing. Speak with a healthcare professional if unsure.",
    seoTitle: "Flaxseed | MossMatrix Ingredient Library",
    seoDescription:
      "Ground flaxseed in MossMatrix sea moss gels: nutty notes and fibre cautions.",
  },
  {
    id: "chia",
    slug: "chia",
    name: "Chia",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Mild nutty seed with gentle texture memory",
    colourHint: "Speckled ivory",
    noteLine: "Seedy · Texture",
    color: "#A89B7E",
    iconKey: "Leaf",
    image: pubIngredientImage("chia-seeds.png"),
    imageAlt: "Chia seeds in a bowl styled for Moss Matrix ingredient library",
    shortDescription:
      "Chia seed can add speckle and slight thickening memory. Best for customers who enjoy textured gels.",
    traditionalUse: "Traditionally used in drinks and puddings in Mesoamerican cuisines.",
    whyAddToSeaMoss:
      "Pairs with flax in the jar for speckle and body; stir finished gel into a fruit smoothie at home if you like. A texture-led choice for people who enjoy a little bite in the spoon.",
    bestWith: "Gold base, flaxseed, vanilla.",
    suggestedBases: "gold",
    routineTags: ["texture rituals"],
    cautions:
      "Dry chia swells: we use it sparingly in gel. If you have swallowing difficulties, avoid whole seeds. Speak with a professional if unsure.",
    seoTitle: "Chia | MossMatrix Ingredient Library",
    seoDescription:
      "Chia seed notes for MossMatrix sea moss gels: texture and cautions.",
  },
  {
    id: "hemp-seed",
    slug: "hemp-seed",
    name: "Hemp Seed",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Nutty, sunflower-like",
    colourHint: "Pale green-fleck",
    noteLine: "Soft nutty",
    color: "#7A8F6B",
    iconKey: "Leaf",
    image: IMG.powder,
    shortDescription:
      "Hulled hemp seed adds soft nuttiness. Popular in shake routines when stirred in at home.",
    traditionalUse: "Traditionally used in whole-food kitchens as a nutty seed topping and in shakes.",
    whyAddToSeaMoss:
      "Pairs with maca and cacao for earthy morning spoons. Herb and seed blend, still gel.",
    bestWith: "Gold base, maca, cacao.",
    suggestedBases: "gold",
    routineTags: ["morning"],
    cautions: "If you take blood thinners or have hemp/cannabis workplace testing concerns, read labels and speak with a professional.",
    seoTitle: "Hemp Seed | MossMatrix Ingredient Library",
    seoDescription:
      "Hemp seed in MossMatrix sea moss gels: nutty profile and cautions.",
  },
  {
    id: "milk-thistle",
    slug: "milk-thistle",
    name: "Milk Thistle",
    category: "Botanicals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Mild, slightly bitter seed powder",
    colourHint: "Soft beige",
    noteLine: "Evening botanical favourite",
    color: "#C4B8A8",
    iconKey: "Sprout",
    image: IMG.herb,
    shortDescription:
      "Milk thistle seed powder is often chosen for gentle, botanical-forward evening blends with a mild, slightly bitter pantry note.",
    traditionalUse: "Traditionally used in European herbal teas and pantry blends.",
    whyAddToSeaMoss:
      "Often chosen when you want a calm, botanical-forward gel to fold into warm milk or tea at home.",
    bestWith: "Dandelion root, burdock root, plain gold base.",
    suggestedBases: "gold",
    routineTags: ["evening", "calm rituals"],
    cautions:
      "May interact with some medicines. If you take regular medication or have liver disease, speak with a GP or pharmacist.",
    seoTitle: "Milk Thistle | MossMatrix",
    seoDescription: "Milk thistle powder in MossMatrix botanical blends: taste and routine wording only.",
  },
  {
    id: "reishi-mushroom",
    slug: "reishi-mushroom",
    name: "Reishi",
    category: "Calm Rituals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Deep, earthy, slightly bitter",
    colourHint: "Warm cocoa dust",
    noteLine: "Soft evening depth",
    color: "#5c4033",
    iconKey: "Moon",
    image: IMG.herb,
    shortDescription:
      "Reishi powder is popular in quiet evening cups for its deep, earthy, slightly bitter profile. We talk taste and ritual, not outcomes.",
    traditionalUse: "Traditionally used in East Asian tonic-style preparations.",
    whyAddToSeaMoss: "Pairs well with cacao and ashwagandha when you want a quieter, richer spoonful.",
    bestWith: "Purple or gold base, cacao, cinnamon.",
    suggestedBases: "both",
    routineTags: ["evening", "calm rituals"],
    cautions: "May interact with blood thinners or immune-modulating therapies. Speak with a healthcare professional if that applies to you.",
    seoTitle: "Reishi | MossMatrix",
    seoDescription: "Reishi mushroom powder for MossMatrix custom herb gels: routine-safe copy.",
  },
  {
    id: "lion-mane-mushroom",
    slug: "lion-mane-mushroom",
    name: "Lion's Mane",
    category: "Calm Rituals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Mild, savoury, mushroom-nutty",
    colourHint: "Pale oat",
    noteLine: "Kitchen curiosity",
    color: "#d4c4b0",
    iconKey: "Sprout",
    image: IMG.herb,
    shortDescription:
      "Lion's mane powder brings a mild, savoury, mushroom-nutty note for curious cooks who like variety in the jar.",
    traditionalUse: "Traditionally used in soups and broths in several cultures.",
    whyAddToSeaMoss: "Adds a savoury note for people who like botanical variety without sweeteners in the jar.",
    bestWith: "Gold base, reishi, cacao.",
    suggestedBases: "gold",
    routineTags: ["evening", "kitchen rituals"],
    cautions: "If you have mushroom allergies, avoid. Speak with a professional if you take neurological medications.",
    seoTitle: "Lion's Mane | MossMatrix",
    seoDescription: "Lion's mane powder for MossMatrix gels: flavour-led education.",
  },
  {
    id: "cordyceps-mushroom",
    slug: "cordyceps-mushroom",
    name: "Cordyceps",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Earthy, umami, faint sweetness",
    colourHint: "Golden tan",
    noteLine: "Morning curiosity",
    color: "#c7a86d",
    iconKey: "Activity",
    image: IMG.herb,
    shortDescription:
      "Cordyceps powder has a rich, earthy, faintly sweet umami note that suits morning cups beside maca or cinnamon. Flavour-led, not a performance product.",
    traditionalUse: "Traditionally used in tonic-style drinks in parts of Asia.",
    whyAddToSeaMoss: "Pairs with maca or matcha when you want a bolder mineral-forward spoonful.",
    bestWith: "Gold base, maca, cinnamon.",
    suggestedBases: "gold",
    routineTags: ["morning"],
    cautions: "If pregnant, breastfeeding, or on medication, speak with a healthcare professional before use.",
    seoTitle: "Cordyceps | MossMatrix",
    seoDescription: "Cordyceps powder in MossMatrix blends: routine wording only.",
  },
  {
    id: "amla",
    slug: "amla",
    name: "Amla",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Tart, bright, fruity-sour powder note",
    colourHint: "Pale gold",
    noteLine: "Bright pantry accent",
    color: "#e8d89a",
    iconKey: "Sun",
    image: IMG.herb,
    shortDescription:
      "Amla fruit powder brings a tart, bright note. It is a powdered botanical for gel, not fresh fruit in the jar.",
    traditionalUse: "Traditionally used in Ayurvedic-inspired kitchen recipes.",
    whyAddToSeaMoss: "Pairs with hibiscus or elderberry for a lively, berry-toned routine without fresh fruit shipping.",
    bestWith: "Purple base, hibiscus, rosehip.",
    suggestedBases: "purple",
    routineTags: ["afternoon", "refresh"],
    cautions: "Very tart in large amounts; start small. Speak with a professional if you take diabetes-related medications.",
    seoTitle: "Amla | MossMatrix",
    seoDescription: "Amla powder for MossMatrix sea moss gels.",
  },
  {
    id: "wheatgrass",
    slug: "wheatgrass",
    name: "Wheatgrass",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Fresh, grassy, intense green",
    colourHint: "Spring green",
    noteLine: "Bold green note",
    color: "#6b8f71",
    iconKey: "Sprout",
    image: IMG.herb,
    shortDescription:
      "Wheatgrass powder is popular for a vivid green colour and bold grassy flavour in morning shakes or coconut water at home.",
    traditionalUse: "Traditionally used in juice-bar style powders at home.",
    whyAddToSeaMoss: "Pairs with spirulina or moringa when you want a deep green spoonful.",
    bestWith: "Gold base, spirulina, nettle.",
    suggestedBases: "gold",
    routineTags: ["green routines"],
    cautions: "Contains gluten-adjacent grasses; avoid if you have relevant allergies. Start with a small amount.",
    seoTitle: "Wheatgrass | MossMatrix",
    seoDescription: "Wheatgrass powder for MossMatrix custom gels.",
  },
  {
    id: "barley-grass",
    slug: "barley-grass",
    name: "Barley Grass",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Mild green, slightly sweet",
    colourHint: "Soft jade",
    noteLine: "Gentle green lift",
    color: "#8faa8c",
    iconKey: "Sprout",
    image: IMG.herb,
    shortDescription:
      "Barley grass powder is often chosen for mellow green routines and pairs well with other mineral-forward botanicals.",
    traditionalUse: "Traditionally used in powdered greens blends.",
    whyAddToSeaMoss: "Softens stronger greens while keeping a bold, earthy plant note on the spoon.",
    bestWith: "Moringa, spirulina, gold base.",
    suggestedBases: "gold",
    routineTags: ["green routines"],
    cautions: "Contains barley; not suitable for coeliac diets. Speak with a professional if unsure.",
    seoTitle: "Barley Grass | MossMatrix",
    seoDescription: "Barley grass powder for MossMatrix gels.",
  },
  {
    id: "green-tea-extract",
    slug: "green-tea-extract",
    name: "Green Tea Extract",
    category: "Superfood Powders",
    type: "powder",
    builderFeatured: false,
    builderAllowed: true,
    flavour: "Clean, tannic, gently grassy",
    colourHint: "Pale jade",
    noteLine: "Bright pantry lift",
    color: "#9aab7b",
    iconKey: "Leaf",
    image: IMG.herb,
    shortDescription:
      "Green tea extract powder adds a clean, tannic, gently grassy note. Often chosen for morning cups alongside other pantry botanicals.",
    traditionalUse: "Traditionally used in tea culture worldwide.",
    whyAddToSeaMoss: "Pairs with matcha or citrus-forward pantry drinks you make at home after stirring gel.",
    bestWith: "Gold base, lemon balm, ginger.",
    suggestedBases: "gold",
    routineTags: ["morning", "refresh"],
    cautions:
      "Contains caffeine from tea solids. Sensitive users should start small. If pregnant or on stimulant-sensitive medications, speak with a healthcare professional.",
    seoTitle: "Green Tea Extract | MossMatrix",
    seoDescription: "Green tea extract powder for MossMatrix blends.",
  },
  {
    id: "garlic",
    slug: "garlic",
    name: "Garlic",
    category: "Botanicals",
    type: "botanical",
    builderFeatured: false,
    builderAllowed: false,
    flavour: "Pungent savoury allium",
    colourHint: "Ivory with speckle",
    noteLine: "Savoury · Educational",
    color: "#C4B8A8",
    iconKey: "CircleDot",
    image: IMG.herb,
    shortDescription:
      "Garlic is included in our ingredient library for education around savoury sea moss habits. It is not offered in the default sweet herb builder.",
    traditionalUse: "Traditionally used in broths, dressings, and savoury kitchens worldwide.",
    whyAddToSeaMoss:
      "Some customers stir plain gel into savoury soups at home. Garlic is a pantry conversation, not a default MossMatrix add-in.",
    bestWith: "Plain gel stirred into soups at home (you add garlic in the kitchen).",
    suggestedBases: "both",
    routineTags: ["savoury education"],
    cautions:
      "May irritate sensitive stomachs and may affect bleeding risk or interact with medications (including anticoagulants). Not appropriate for sweet gel blends. Speak with a healthcare professional if unsure.",
    seoTitle: "Garlic | MossMatrix (education note)",
    seoDescription:
      "Why MossMatrix does not sell garlic in the default sweet herb builder: savoury education, medication cautions, and powder-only gel philosophy.",
  },
];

const _INGREDIENT_CATALOG_WITH_LAUNCH = _INGREDIENT_CATALOG_RAW.map((row) => {
  const blendAvailable = BLEND_AVAILABLE_IDS.has(row.id);
  const launchFavourite = LAUNCH_FAVOURITE_IDS.includes(row.id);
  return {
    ...row,
    blendAvailable,
    launchHerb: blendAvailable,
    libraryOnly: !blendAvailable,
    builderAllowed: blendAvailable,
    builderFeatured: launchFavourite,
    availableInBuilder: blendAvailable,
  };
});

const CATALOG = attachIngredientLibraries(_INGREDIENT_CATALOG_WITH_LAUNCH);

export const ALL_INGREDIENTS = CATALOG;

const byId = Object.fromEntries(CATALOG.map((i) => [i.id, i]));
const bySlug = Object.fromEntries(CATALOG.map((i) => [i.slug, i]));

/** @param {string} slug */
export function getIngredientBySlug(slug) {
  return bySlug[slug] || null;
}

/** @param {string} id */
export function getIngredientById(id) {
  return byId[id] || null;
}

/** Ingredients customer can add in builder (launch herbs only). */
export function getBuilderEligibleCatalog() {
  return CATALOG.filter((i) => i.blendAvailable);
}

function slimForBuilder(i) {
  const Icon = ICON_MAP[i.iconKey] || Leaf;
  return {
    id: i.id,
    slug: i.slug,
    name: i.name,
    note: i.noteLine,
    tagline: i.flavour,
    color: i.color,
    image: i.image,
    imageAlt: i.imageAlt,
    icon: Icon,
    category: i.category,
    routineTags: i.routineTags,
  };
}

const eligible = getBuilderEligibleCatalog();
const eligibleById = Object.fromEntries(eligible.map((i) => [i.id, i]));

/** @param {string} filterId */
export function ingredientMatchesLibraryFilter(ing, filterId) {
  if (!filterId || filterId === "All") return true;
  const tab = INGREDIENT_LIBRARY_FILTERS.find((f) => f.id === filterId);
  return tab?.match ? tab.match(ing) : true;
}

/** @param {string} id */
export function isBlendAvailableHerbId(id) {
  return !!getIngredientById(id)?.blendAvailable;
}

/**
 * Keep only launch blend herbs (max 3). Returns removed display names for cart/share notices.
 * @param {string[]} ids
 */
export function filterBlendAvailableHerbIds(ids) {
  const herbs = [];
  const removed = [];
  for (const id of [...new Set((ids || []).map(String).filter(Boolean))]) {
    if (isBlendAvailableHerbId(id)) {
      if (herbs.length < 3) herbs.push(id);
    } else {
      const name = getIngredientById(id)?.name || id;
      if (!removed.includes(name)) removed.push(name);
    }
  }
  return { herbs, removed };
}

/** Featured row order (premium builder) */
export const BUILDER_HERBS_FEATURED = LAUNCH_FAVOURITE_IDS.map((id) => eligibleById[id])
  .filter(Boolean)
  .map(slimForBuilder);

/** All builder cards (for modal + drag sources) */
export const BUILDER_HERBS_ALL = eligible.map(slimForBuilder);

/**
 * @deprecated Prefer BUILDER_HERBS_ALL; kept for GlassBlender / FinishedJar imports via data.js
 * Full list of builder-allowed herbs for tint + label resolution.
 */
export const INGREDIENTS = BUILDER_HERBS_ALL;
